/*
 * Mobile subheader carousel (Trustpilot + USP messages).
 * Native horizontal scroll for reliable touch swiping; autoplay uses scrollLeft.
 */
class SfwSubheaderUspSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.advanceTimer = null;
    this.resumeTimeout = null;
    this.scrollSettleTimer = null;
    this.isPausedByUser = false;
    this.isProgrammaticScroll = false;
    this.onMediaChange = () => this.init();
    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.resumeAfterUserPause();
      }
    };
    this.onScroll = () => this.handleScroll();
    this.onTouchStart = () => this.pauseForUser();
    this.onPointerDown = () => this.pauseForUser();
  }

  connectedCallback() {
    this.list = this.querySelector('.sfw-subheader-trust__slider-list');
    if (!this.list) return;

    this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.mobileQuery.addEventListener('change', this.onMediaChange);
    this.reducedMotion.addEventListener('change', this.onMediaChange);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    this.list.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.list.addEventListener('pointerdown', this.onPointerDown, { passive: true });

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.mobileQuery.matches || !this.slides || this.slides.length < 2) return;
      this.scrollToSlide(this.currentIndex, false);
    });
    this.resizeObserver.observe(this);

    if (document.readyState === 'complete') {
      this.deferInit();
    } else {
      window.addEventListener('load', () => this.deferInit(), { once: true });
      this.deferInit();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.onMediaChange);
    if (this.reducedMotion) this.reducedMotion.removeEventListener('change', this.onMediaChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.list) {
      this.list.removeEventListener('scroll', this.onScroll);
      this.list.removeEventListener('touchstart', this.onTouchStart);
      this.list.removeEventListener('pointerdown', this.onPointerDown);
    }
    clearTimeout(this.resumeTimeout);
    clearTimeout(this.scrollSettleTimer);
  }

  deferInit() {
    requestAnimationFrame(() => requestAnimationFrame(() => this.init()));
  }

  init() {
    this.stopAutoplay();
    clearTimeout(this.resumeTimeout);
    clearTimeout(this.scrollSettleTimer);
    this.isPausedByUser = false;
    this.isProgrammaticScroll = false;

    this.slides = Array.from(this.list.querySelectorAll('.sfw-subheader-trust__slide'));

    if (!this.mobileQuery.matches || this.slides.length < 2) {
      this.list.scrollLeft = 0;
      return;
    }

    this.currentIndex = this.getNearestSlideIndex();
    this.scrollToSlide(this.currentIndex, false);
    this.notifyActiveSlide();
    this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();

    if (!this.mobileQuery.matches || !this.slides || this.slides.length < 2) return;
    if (this.reducedMotion.matches || document.hidden || this.isPausedByUser) return;

    this.scheduleNextAdvance();
  }

  scheduleNextAdvance() {
    clearTimeout(this.advanceTimer);
    this.advanceTimer = setTimeout(() => {
      this.advance();
      this.scheduleNextAdvance();
    }, this.speed);
  }

  stopAutoplay() {
    clearTimeout(this.advanceTimer);
    this.advanceTimer = null;
  }

  advance() {
    if (!this.slides || this.slides.length < 2) return;
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.scrollToSlide(this.currentIndex, true);
  }

  getSlideLeft(index) {
    var slide = this.slides[index];
    if (!slide) return 0;
    return slide.offsetLeft;
  }

  getNearestSlideIndex() {
    if (!this.slides || !this.slides.length) return 0;

    var scrollLeft = this.list.scrollLeft;
    var nearestIndex = 0;
    var nearestDistance = Infinity;

    for (var i = 0; i < this.slides.length; i++) {
      var distance = Math.abs(this.getSlideLeft(i) - scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }

    return nearestIndex;
  }

  scrollToSlide(index, smooth) {
    if (!this.list || !this.slides || !this.slides.length) return;

    var safeIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this.currentIndex = safeIndex;
    var targetLeft = this.getSlideLeft(safeIndex);

    this.isProgrammaticScroll = true;
    this.list.style.scrollBehavior = smooth && !this.reducedMotion.matches ? 'smooth' : 'auto';
    this.list.scrollLeft = targetLeft;

    // Fallback for engines that ignore scrollLeft while snap is settling.
    if (Math.abs(this.list.scrollLeft - targetLeft) > 2) {
      try {
        this.list.scrollTo({
          left: targetLeft,
          behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'auto',
        });
      } catch (error) {
        this.list.scrollLeft = targetLeft;
      }
    }

    clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = setTimeout(() => {
      this.isProgrammaticScroll = false;
      this.list.style.scrollBehavior = '';
      this.currentIndex = this.getNearestSlideIndex();
      this.notifyActiveSlide();
    }, smooth ? 450 : 50);
  }

  notifyActiveSlide() {
    if (!this.slides || !this.slides.length) return;
    var slide = this.slides[this.currentIndex];
    if (!slide) return;
    document.dispatchEvent(
      new CustomEvent('sfw:subheader-slide-visible', {
        detail: { slide: slide, index: this.currentIndex },
      })
    );
  }

  handleScroll() {
    if (this.isProgrammaticScroll) return;

    clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = setTimeout(() => {
      this.currentIndex = this.getNearestSlideIndex();
      this.notifyActiveSlide();
    }, 80);
  }

  pauseForUser() {
    if (!this.mobileQuery.matches || !this.slides || this.slides.length < 2) return;

    this.isPausedByUser = true;
    this.stopAutoplay();
    clearTimeout(this.resumeTimeout);

    if (this.reducedMotion.matches) return;

    this.resumeTimeout = setTimeout(() => {
      this.isPausedByUser = false;
      this.currentIndex = this.getNearestSlideIndex();
      this.startAutoplay();
    }, this.speed * 2);
  }

  resumeAfterUserPause() {
    if (!this.isPausedByUser) {
      this.startAutoplay();
    }
  }
}

if (!customElements.get('sfw-subheader-usp-slider')) {
  customElements.define('sfw-subheader-usp-slider', SfwSubheaderUspSlider);
}
